import type Quill from "quill";
import { eventEmitter } from "@/eventEmitter";
import { Group } from "@/group";
import { TextWidgetV2 } from "../textV2";
import { CanvasLinkHoverResolver } from "./CanvasLinkHoverResolver";
import { EditorLinkResolver } from "./EditorLinkResolver";
import type {
    ActiveLinkRef,
    LinkPopupInternalState,
    LinkPopupViewModel,
    ScenePoint,
} from "./types";

const LINK_POPUP_SHOW_DELAY = 80;
const LINK_POPUP_HIDE_DELAY = 120;
const LINK_POPUP_ANCHOR_GAP = 8;

export class LinkPopupController {
    private state: LinkPopupInternalState = {
        visibility: "hidden",
        link: null,
        isOverAnchor: false,
        isOverPopup: false,
        lastSource: null,
    };

    private hideTimer: number | null = null;
    private showTimer: number | null = null;
    private frame: number | null = null;
    private lastViewModel: LinkPopupViewModel | null = null;

    private editorResolver: EditorLinkResolver | null = null;
    private canvasResolver: CanvasLinkHoverResolver;

    constructor() {
        this.canvasResolver = new CanvasLinkHoverResolver();
    }

    attachEditorSession(widgetId: string, quill: Quill) {
        this.cancelShow();
        this.cancelHide();
        this.editorResolver = new EditorLinkResolver(widgetId, quill);
    }

    detachEditorSession() {
        this.editorResolver = null;
        if (this.state.lastSource === "editing") {
            this.closeCurrent(true);
        }
    }

    onEditorSelectionChange() {
        if (!this.editorResolver) {
            return;
        }

        const link = this.editorResolver.resolveActiveLink();
        this.cancelHide();

        if (!link) {
            this.setAnchorHover(false);
            this.maybeHide();
            return;
        }

        this.setState({
            visibility: "hover",
            link,
            isOverAnchor: true,
            lastSource: "editing",
        });
    }

    onEditorTextChange() {
        this.scheduleFrame(() => this.onEditorSelectionChange());
    }

    onCanvasPointerMove(candidate: Group | null, scenePoint: ScenePoint) {
        if (this.editorResolver) {
            return;
        }

        const widget = this.getHoveredTextWidgetFromCandidate(candidate);

        if (!widget) {
            this.setAnchorHover(false);
            this.maybeHide();
            return;
        }

        const link = this.canvasResolver.hitTest(widget, scenePoint);

        if (!link) {
            this.setAnchorHover(false);
            this.maybeHide();
            return;
        }

        this.setAnchorHover(true);

        if (this.state.link?.anchor.key === link.anchor.key) {
            this.setState({ link, lastSource: "viewing" });
            return;
        }

        this.startShowTimer(() => {
            this.setState({
                visibility: "hover",
                link,
                lastSource: "viewing",
            });
        });
    }

    onCanvasPointerLeave() {
        if (this.editorResolver) {
            return;
        }

        this.setAnchorHover(false);
        this.maybeHide();
    }

    onPopupPointerEnter = () => {
        this.cancelHide();
        this.setState({ isOverPopup: true });
    };

    onPopupPointerLeave = () => {
        this.setState({ isOverPopup: false });
        this.maybeHide();
    };

    onViewportTransformChange() {
        if (!this.state.link) {
            return;
        }

        this.scheduleFrame(() => {
            this.emit();
        });
    }

    onEditorPointerLinkEnter(link: ActiveLinkRef) {
        this.cancelHide();
        this.setState({
            visibility: "hover",
            link,
            isOverAnchor: true,
            lastSource: "editing",
        });
    }

    onEditorPointerLinkEnterByElement(linkEl: HTMLAnchorElement) {
        const link = this.editorResolver?.resolveFromDomLink(linkEl);
        if (!link) {
            this.onEditorPointerLinkLeave();
            return;
        }

        this.onEditorPointerLinkEnter(link);
    }

    onEditorPointerLinkLeave() {
        // если selection всё ещё на ссылке, можно не закрывать сразу
        if (this.editorResolver) {
            const selectionLink = this.editorResolver.resolveActiveLink();
            if (selectionLink) {
                this.setState({
                    visibility: "hover",
                    link: selectionLink,
                    isOverAnchor: true,
                    lastSource: "editing",
                });
                return;
            }
        }

        this.setAnchorHover(false);
        this.maybeHide();
    }

    onEditorLinkClickByElement(linkEl: HTMLAnchorElement) {
        this.onEditorPointerLinkEnterByElement(linkEl);
    }

    destroy() {
        this.cancelHide();
        this.cancelShow();

        if (this.frame != null) {
            cancelAnimationFrame(this.frame);
            this.frame = null;
        }

        this.editorResolver = null;
    }

    closeCurrent(force = false) {
        if (!force) {
            if (this.state.isOverAnchor || this.state.isOverPopup) {
                return;
            }
        }

        this.cancelHide();
        this.cancelShow();
        this.setState({
            visibility: "hidden",
            link: null,
            isOverAnchor: false,
            isOverPopup: false,
            lastSource: null,
        });
    }

    private setAnchorHover(value: boolean) {
        this.cancelShow();
        if (value) this.cancelHide();
        this.setState({ isOverAnchor: value });
    }

    private maybeHide() {
        if (this.state.isOverAnchor || this.state.isOverPopup) {
            return;
        }

        this.cancelHide();
        this.hideTimer = window.setTimeout(() => {
            this.closeCurrent(true);
        }, LINK_POPUP_HIDE_DELAY);
    }

    private startShowTimer(cb: () => void) {
        this.cancelShow();
        this.showTimer = window.setTimeout(cb, LINK_POPUP_SHOW_DELAY);
    }

    private scheduleFrame(cb: () => void) {
        if (this.frame != null) {
            return;
        }

        this.frame = requestAnimationFrame(() => {
            this.frame = null;
            cb();
        });
    }

    private cancelHide() {
        if (this.hideTimer != null) {
            clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }
    }

    private cancelShow() {
        if (this.showTimer != null) {
            clearTimeout(this.showTimer);
            this.showTimer = null;
        }
    }

    private setState(patch: Partial<LinkPopupInternalState>) {
        const next = { ...this.state, ...patch };

        if (
            next.visibility === this.state.visibility &&
            next.isOverAnchor === this.state.isOverAnchor &&
            next.isOverPopup === this.state.isOverPopup &&
            next.lastSource === this.state.lastSource &&
            next.link?.anchor.key === this.state.link?.anchor.key
        ) {
            return;
        }

        this.state = next;
        this.emit();
    }

    private emit() {
        const next = this.getViewModel();

        if (
            this.lastViewModel?.url === next?.url &&
            this.lastViewModel?.left === next?.left &&
            this.lastViewModel?.top === next?.top
        ) {
            return;
        }

        this.lastViewModel = next;

        eventEmitter.dispatch("link.popup.state", {
            state: next,
        });
    }

    private getViewModel(): LinkPopupViewModel | null {
        if (!this.isVisible()) {
            return null;
        }

        const anchorRect = this.state.link?.anchor.getBoundingClientRect();
        if (!anchorRect) {
            return null;
        }

        const url = this.state.link?.url;
        if (!url) {
            return null;
        }

        return {
            url,
            left: Math.round(anchorRect.left + anchorRect.width / 2),
            top: Math.round(anchorRect.bottom + LINK_POPUP_ANCHOR_GAP),
        };
    }

    private isVisible(state: LinkPopupInternalState = this.state) {
        return state.visibility !== "hidden" && state.link !== null;
    }

    private getHoveredTextWidgetFromCandidate(candidate: Group | null): TextWidgetV2 | null {
        if (!candidate) {
            return null;
        }

        if (candidate.isItemWrapper()) {
            const child = candidate.onlyChild();
            return child instanceof TextWidgetV2 && child.isOver() ? child : null;
        }

        for (let index = candidate.children.length - 1; index >= 0; index--) {
            const childGroup = candidate.children[index];

            if (!(childGroup instanceof Group) || !childGroup.isItemWrapper()) {
                continue;
            }

            const child = childGroup.onlyChild();
            if (child instanceof TextWidgetV2 && child.isOver()) {
                return child;
            }
        }

        return null;
    }
}
